<template lang="pug">
    v-dialog.timerDialog(
        v-model="dialog"
        full-screen
        content-class="timerModal"
    )

        // Overlay
        div.backgroundVideoOverflow(:style="{ 'backgroundImage': `url(${timerSettings.imageUrl})` }")
            div

        transition(name="slide-y-fade")
            div.timerModalInner(v-if="timerCounter")

                div.timer

                    div.timerControls

                        button-component.mx-0(
                            v-if="timerSettings.soundUrl"
                            color="white"
                            :action="toggleBackgroundSound"
                            outline
                        )
                            span {{ muteSound ? 'Sound on' : 'Sound off' }}
                            v-icon(v-if="muteSound" color="white") volume_off
                            v-icon(v-else color="white") volume_up


                        // Run/Pause timer
                        button-component.mr-0(
                            color="white"
                            :action="toggleRunTimer"
                            outline
                        ) {{ isPaused ? $t('defaultButtons.resume') : $t('defaultButtons.pause') }}


                    // Timer circle
                    div.timerCounter
                        // Progress circle
                        radial-progress-bar(
                            :diameter="260"
                            :strokeWidth="12"
                            :innerStrokeWidth="12"
                            :completed-steps="timerCounter / 60"
                            :total-steps="timerSettings.timer"
                            :animateSpeed="2000"
                            :isClockwise="true"
                            innerStrokeColor="#ffffff"
                            startColor="#7096D2"
                            stopColor="#7096D2"
                        )

                        // Countdown
                        div.timerCountdown {{ countdown }}

                div.text-xs-center.mt-5.full-width

                    button-component.timerFinish(
                        color="action"
                        block
                        large
                        :action="finishTimer"
                    ) {{ $t('defaultButtons.finish') }}
</template>

<script>
    import VueTypes from 'vue-types';
    import Button from '~components/Button';
    import RadialProgressBar from 'vue-radial-progress';
    import sessions from '~api/sessions';
    import { mapActions, mapGetters } from 'vuex';

    export default {
        components: {
            RadialProgressBar,
            ButtonComponent: Button
        },
        props: {
            timerSettings: VueTypes.object.def({}),
            dialogOpen: VueTypes.bool.def(false),
            timerCallback: VueTypes.func.def(() => {})
        },
        data() {
            return {
                isWatched: false,
                dialog: true,
                timerCounter: 0,
                timeMeditated: null,
                timerInterval: null,
                soundRepeatInterval: null,
                intervalSound: null,
                backgroundSound: null,
                muteSound: false,
                isPaused: false,
                sessionViewId: null
            };
        },

        computed: {
            ...mapGetters('auth', ['isMember']),
            ...mapGetters('sessions', ['moodBefore', 'session']),
            countdown() {
                const hours =
                    this.timerCounter >= 3600 &&
                    parseInt(this.timerCounter / 3600);

                return `${hours ? `${hours} h, ` : ''} ${
                    this.timerCounter >= 60
                        ? `${parseInt(
                              (this.timerCounter - hours * 3600) / 60
                          )} m, `
                        : ''
                } ${this.timerCounter % 60} s`;
            }
        },
        mounted() {
            // Init Timer
            this.timerCounter = this.timerSettings?.timer * 60;
            this.initTimer();

            // Init saving viewing time
            this.saveViewSession();

            // Init playing interval sound
            this.intervalSound = new Audio(
                'https://s3.us-east-2.amazonaws.com/chillanywhere.com/timer/sounds/386429-Meditation_Bell_-Dynamic_-Aston_Stealth_003.mp3'
            );
            this.initPlayIntervalSound();

            // Init background sound
            if (this.timerSettings?.soundUrl) {
                this.initBackgroundSound();
            }
        },
        beforeDestroy() {
            this.clearAllIntervals();

            if (!this.isWatched)
                sessions.setWatched(process.env.VUE_APP_TIMER_SESSION_ID);
        },
        methods: {
            ...mapActions('sessions', ['setShowMood']),

            // Decrease timer counter
            updateTimer() {
                if (this.timerCounter === 1) {
                    this.playIntervalSound();
                    this.clearAllIntervals();
                    this.finishTimer();
                    sessions.setWatched(process.env.VUE_APP_TIMER_SESSION_ID);
                    this.isWatched = true;
                }

                this.timerCounter--;
            },

            initBackgroundSound() {
                this.backgroundSound = new Audio(this.timerSettings?.soundUrl);
                if (typeof this.backgroundSound.loop == 'boolean') {
                    this.backgroundSound.loop = true;
                } else {
                    this.backgroundSoundListener = this.backgroundSound.addEventListener(
                        'ended',
                        () => this.backgroundSound.play(),
                        false
                    );
                }

                !this.muteSound && this.backgroundSound.play();
            },
            // Stop/Play background sound
            toggleBackgroundSound() {
                if (!this.backgroundSound) return;
                this.muteSound = !this.muteSound;

                return this.muteSound
                    ? this.backgroundSound.pause()
                    : this.backgroundSound.play();
            },

            // Play interval sound
            playIntervalSound() {
                if (!this.timerCounter) return;

                this.intervalSound.play();
            },

            // Clear defined intervals
            clearAllIntervals() {
                clearInterval(this.timerInterval);
                if (this.soundRepeatInterval)
                    clearInterval(this.soundRepeatInterval);

                this.timerInterval = null;
                this.soundRepeatInterval = null;

                if (this.backgroundSound) {
                    this.backgroundSound.pause();
                    this.backgroundSound = null;
                }
            },

            // Init timer
            initTimer() {
                this.timerInterval = setInterval(() => {
                    this.updateTimer();
                    this.saveMeditatingTime();
                }, 1000);
            },

            // Init playing interval sound
            initPlayIntervalSound() {
                if (!this.timerSettings?.interval) return;

                this.soundRepeatInterval = setInterval(
                    () => this.playIntervalSound(),
                    this.timerSettings?.interval * 60 * 1000
                );
            },

            // Run/pause timer
            toggleRunTimer() {
                this.isPaused = !this.isPaused;
                if (this.timerInterval) return this.clearAllIntervals();

                this.initTimer();
                this.initPlayIntervalSound();
                this.initBackgroundSound();
            },

            // Stop timer and close modal window
            finishTimer() {
                if (
                    this.moodBefore &&
                    !this.session.todayVote.isSkip &&
                    !this.session.todayVote.isCompleted
                )
                    this.setShowMood();

                this.toggleBackgroundSound();
                this.saveMeditatingTime(true);
                this.timerCallback();
            },

            /**
             * Save viewed time
             */
            async saveViewSession() {
                // View data for meditation
                let viewData = {
                    time: '0',
                    sessionId: process.env.VUE_APP_TIMER_SESSION_ID,
                    type: 'recorded'
                };

                // Send view data for statistics when set after mood
                if (this.isMember) {
                    const resp = await sessions.saveWatchedTime(viewData);

                    if (resp.ok) this.sessionViewId = resp.data.id;
                }
            },

            // Save watching time
            saveMeditatingTime(force = false) {
                if (
                    !force &&
                    this.timerSettings?.timer * 60 -
                        this.timerCounter -
                        this.timeMeditated <
                        10
                )
                    return;

                this.timeMeditated =
                    this.timerSettings?.timer * 60 - this.timerCounter;

                // View data for meditation
                let viewData = {
                    id: this.sessionViewId,
                    time: this.timeMeditated.toString(),
                    sessionId: process.env.VUE_APP_TIMER_SESSION_ID,
                    type: 'recorded'
                };

                // Send view data for statistics when set after mood
                if (this.isMember) {
                    sessions.updateWatchedTime(viewData);
                }
            }
        }
    };
</script>

<style lang="stylus" scoped>
    @import "../../../assets/style/_variables.styl"

    .timerControls
        display: flex

        > *
            flex: 1 1 50%
            min-width: auto

    .backgroundVideoOverflow
        z-index: 2
        width: 100vw
        height: 100vh
        position: fixed
        top: 0
        left: 0
        background-size: cover
        background-position: center

        div
            width: 100%
            height: 100%
            background-color: rgba(2, 35, 65, 0.6)

    .backgroundVideo
        z-index: 1
        object-fit: cover
        filter: blur(6px)


    >>> .timerModal
        width: 100vw
        height: 100vh
        max-height: 100vh
        overflow: hidden
        display: flex
        justify-content: center
        align-items: center

    .timerModalInner
        z-index: 2
        width: 320px;
        padding: 30px 15px
        border-radius: 10px

    .timerCountdown
        white-space: nowrap
        font-size: 34px
        font-weight: 300
        position: absolute
        top: 50%
        left: 50%
        transform: translate(-50%, -50%)

    .timerModalInner
    .timer
        display: flex
        flex-direction: column
        align-items: center

    .timerCounter
        width: 280px
        height: 280px
        position: relative
        display: flex
        justify-content: center
        align-items: center
        margin-top: 40px
        padding: 10px
        border-radius: 50%
        background-color: rgba(255, 255, 255, 0.1)

    .toggleBackgroundSound
        z-index: 4
        width: 50px
        height: 50px
        border-radius: 50px
        position: absolute
        left: 0
        top: 0
        display: flex
        align-items: center
        justify-content: center
        border: 2px solid $theme.white
        cursor: pointer

    @media screen and (max-width: $grid-breakpoints.sm - 1)
        .timerModalInner
        .timer
        .timerControls
            width: 100%

        .timerFinish
            font-size: 17px
            padding: 17px 30px
</style>
